<template>
  <div>
    <RevContentBlock
      :id="textToTarget(contentTitle, 'heading-2')"
      :button-label
      button-variant="primary"
      :class="dynamicClasses"
      :has-rounded-container="!!props.backgroundColor"
      :has-rounded-image="true"
      :image-left="isContentRight"
      :image-props="image"
      :rel="ctaLink?.link?.rel"
      :surtitle
      :target="ctaLink?.link?.target"
      :title="contentTitle"
      :title-variant="contentTitleType"
      :to="ctaLink?.link?.href"
      @click="handleCtaLinkClick"
    >
      <RichText
        v-if="contentText"
        class="whitespace-pre-line"
        :class="[$style.richTextSpacing]"
        :rich-text="contentText"
        style-variant="none"
      />
    </RevContentBlock>

    <YoutubeVideoModal
      v-if="ctaVideo"
      :name="videoModalName"
      :title="ctaVideo.title"
      :youtube-id="ctaVideo.id"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useCssModule } from 'vue'

import type { ContentMediaProps } from '@backmarket/http-api/src/api-specs-content/models/content-media'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { tw } from '@backmarket/utils/string/tw'
import { RevContentBlock } from '@ds/components/ContentBlock'
import { openModal } from '@ds/components/ModalBase'

import type { ContentBlockProps } from '../../models/content-block'
import YoutubeVideoModal from '../../shared-components/YoutubeVideoModal/YoutubeVideoModal.vue'
import { textToTarget } from '../../utils/textToTarget'
import RichText from '../RichText/RichText.vue'

export type ContentMediaBlockProps = ContentMediaProps & ContentBlockProps

const props = withDefaults(defineProps<ContentMediaBlockProps>(), {
  tracking: () => ({}),
  contentTitle: '',
  contentTitleType: 'title2',
})

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error'])

const { trackClick } = useTracking()
const styles = useCssModule()

const hasCTALink = computed(() => !isEmpty(props.ctaLink))

const videoModalName = computed(() => `${props.ctaVideo?.id}-video-modal`)
const buttonLabel = computed(() =>
  hasCTALink.value ? props.ctaLink?.label : props.ctaVideo?.label,
)

const isContentRight = computed(() => props.textPosition === 'right')

const dynamicClasses = computed(() => {
  const hasCTA = props.ctaLink || props.ctaVideo
  const isBackgroundActive = props.backgroundColor === 'white'

  return [
    isContentRight.value ? tw`md:p-0 md:pr-24` : tw`md:p-0 md:pl-24`,
    isBackgroundActive && hasCTA && tw`p-24 shadow-short`,
    isBackgroundActive && styles.imageBorderType,
    isBackgroundActive && tw`bg-float-default-low`,
  ]
})

const hasCtaVideo = computed(() => !isEmpty(props.ctaVideo))

function sendTrackingData() {
  trackClick({
    ...props.tracking,
    ...(props.contentTitle && { blockTitle: props.contentTitle }),
  })
}

function openVideoModal() {
  openModal(videoModalName.value)
}

function handleCtaLinkClick() {
  sendTrackingData()
  if (hasCtaVideo.value) openVideoModal()
}
</script>

<style module>
.imageBorderType {
  div > img {
    @apply !rounded-md md:!rounded-none;
  }
}

.richTextSpacing {
  div {
    @apply flex flex-col gap-12;
  }

  p:nth-last-of-type(1):empty {
    display: none;
  }
}
</style>
